import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle, ChevronRight, GitBranch, Pencil, Plus, X, AlertTriangle } from "lucide-react";

interface RouteStage {
  stage:
    | "finance_review"
    | "hr_review"
    | "sport_admin_review"
    | "ad_review"
    | "compliance_review"
    | "legal_review"
    | "signature";
  approvers: string[];
  quorum: "all" | "any";
  skipped?: boolean;
  skipReason?: string;
}

const STAGE_LABEL: Record<string, string> = {
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  compliance_review: "Compliance Review",
  ad_review: "AD Review",
  signature: "Signature",
};

const STAGE_DESCRIPTION: Record<string, string> = {
  finance_review: "Finance team verifies budget and compensation terms",
  hr_review: "HR confirms benefits, classification and policy compliance",
  sport_admin_review: "Sport administrator for the contract's sport signs off",
  legal_review: "Legal counsel reviews clauses, risk and enforceability",
  compliance_review: "Compliance verifies policy and regulatory requirements",
  ad_review: "Athletic Director final sign-off",
  signature: "External countersign collection",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: number;
  hasComplianceBlockers: boolean;
  isAmendment: boolean;
  onSubmitted: () => void;
}

export function RouteForApprovalDialog({ open, onOpenChange, contractId, hasComplianceBlockers, isAmendment, onSubmitted }: Props) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [route, setRoute] = useState<RouteStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingStage, setEditingStage] = useState<string | null>(null);
  const [newApprover, setNewApprover] = useState("");

  const loadRoute = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const preview = (data.previewRoute ?? data.approval?.resolvedRoute ?? []) as RouteStage[];
        setRoute(preview);
      }
    } finally {
      setLoading(false);
    }
  }, [open, contractId, getToken]);

  useEffect(() => { void loadRoute(); }, [loadRoute]);

  function toggleSkip(stageKey: string) {
    setRoute((prev) =>
      prev.map((s) =>
        s.stage === stageKey
          ? { ...s, skipped: !s.skipped, skipReason: !s.skipped ? "Manually skipped by drafter" : undefined }
          : s
      )
    );
  }

  function addApprover(stageKey: string) {
    const email = newApprover.trim().toLowerCase();
    if (!email.includes("@")) return;
    setRoute((prev) =>
      prev.map((s) =>
        s.stage === stageKey && !s.approvers.includes(email)
          ? { ...s, approvers: [...s.approvers, email] }
          : s
      )
    );
    setNewApprover("");
  }

  function removeApprover(stageKey: string, email: string) {
    setRoute((prev) =>
      prev.map((s) =>
        s.stage === stageKey
          ? { ...s, approvers: s.approvers.filter((a) => a !== email) }
          : s
      )
    );
  }

  async function handleSubmit() {
    if (hasComplianceBlockers) {
      toast({
        title: "Compliance blockers must be resolved first",
        description: "Fix all red compliance rules before routing for approval.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/approvals/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isAmendment, customRoute: route }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const blockers = (data as { blockers?: string[] }).blockers;
        if (blockers?.length) {
          toast({
            title: "Submission blocked",
            description: blockers[0],
            variant: "destructive",
          });
        } else {
          throw new Error((data as { error?: string }).error ?? `Error ${res.status}`);
        }
        return;
      }
      toast({ title: isAmendment ? "Amendment submitted" : "Routed for approval", description: "The contract has been sent to the first reviewer." });
      onOpenChange(false);
      onSubmitted();
    } catch (err) {
      toast({ title: "Submit failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  const activeStages = route.filter((s) => !s.skipped);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            {isAmendment ? "Route Amendment for Approval" : "Route for Approval"}
          </DialogTitle>
          <DialogDescription>
            Review the suggested approval chain below. You can adjust approvers or skip optional stages before sending.
          </DialogDescription>
        </DialogHeader>

        {hasComplianceBlockers && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This contract has unresolved compliance blockers. Resolve them in the Compliance panel before routing for approval.
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="space-y-3 py-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {route.map((stage, idx) => (
              <div
                key={stage.stage}
                className={`rounded-lg border p-4 transition-colors ${stage.skipped ? "bg-muted/30 border-dashed opacity-60" : "bg-background"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold ${stage.skipped ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}`}>
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div className={`font-medium text-sm ${stage.skipped ? "line-through text-muted-foreground" : ""}`}>
                        {STAGE_LABEL[stage.stage]}
                      </div>
                      <div className="text-xs text-muted-foreground">{STAGE_DESCRIPTION[stage.stage]}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {stage.skipped ? (
                      <Badge variant="outline" className="text-[10px]">Skipped</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">
                        {stage.quorum === "all" ? "All must approve" : "Any one approves"}
                      </Badge>
                    )}
                    {stage.stage !== "signature" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => toggleSkip(stage.stage)}
                      >
                        {stage.skipped ? "Include" : "Skip"}
                      </Button>
                    )}
                  </div>
                </div>

                {!stage.skipped && (
                  <div className="mt-3 space-y-2">
                    {stage.approvers.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {stage.approvers.map((email) => (
                          <div key={email} className="flex items-center gap-1 bg-muted rounded-full px-2.5 py-1 text-xs">
                            <span className="font-mono">{email}</span>
                            {editingStage === stage.stage && (
                              <button
                                type="button"
                                onClick={() => removeApprover(stage.stage, email)}
                                className="text-muted-foreground hover:text-destructive ml-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">
                        No specific approvers — any admin can approve this stage.
                      </p>
                    )}

                    {editingStage === stage.stage ? (
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="approver@email.com"
                          value={newApprover}
                          onChange={(e) => setNewApprover(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addApprover(stage.stage); } }}
                          className="h-7 text-xs"
                        />
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addApprover(stage.stage)}>
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditingStage(null); setNewApprover(""); }}>
                          Done
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-muted-foreground hover:text-foreground p-0"
                        onClick={() => setEditingStage(stage.stage)}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Edit approvers
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeStages.length > 0 && !loading && (
          <>
            <Separator />
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              <span className="font-medium text-foreground">Flow:</span>
              {activeStages.map((s, i) => (
                <span key={s.stage} className="flex items-center gap-1.5">
                  <span>{STAGE_LABEL[s.stage]}</span>
                  {i < activeStages.length - 1 && <ChevronRight className="h-3.5 w-3.5" />}
                </span>
              ))}
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-green-700 font-medium flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Executed
              </span>
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || hasComplianceBlockers || loading}
            className="min-w-[120px]"
          >
            {submitting ? "Routing…" : hasComplianceBlockers ? (
              <span className="flex items-center gap-1.5"><XCircle className="h-4 w-4" /> Blocked</span>
            ) : (
              <span className="flex items-center gap-1.5"><GitBranch className="h-4 w-4" /> Route for Approval</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
